import {
  ENTITLEMENTS,
  EntitlementId,
} from '~/purchases/contexts/purchases/Purchases';

/**
 * Single source of truth for the subscription / billing page.
 *
 * The page (tabs, feature lists, prices, purchase buttons) is rendered entirely
 * from this config — add a plan or feature here, not in the screen. Human-readable
 * copy lives in the `subscription` i18n namespace; `featureKeys` are the leaf keys
 * under `plans.<id>.features` for each tier (see
 * `features/i18n/resources/locales/{en,sk}/subscription.json`).
 *
 * The `packages` map ties each plan + billing period to a RevenueCat package
 * `lookup_key` from the `default` offering (RC project `projc61f0c62`). Prices are
 * read live from the resolved package (`pkg.product.priceString`); the config never
 * hardcodes an amount.
 *
 * The `Free` tier from the design (Pencil `w5TdK`) is intentionally excluded — this
 * page only sells the paid upgrades.
 */

export type BillingPeriod = 'monthly' | 'yearly';

/** Billing periods, in display order. `yearly` carries the "save" badge. */
export const BILLING_PERIODS: readonly BillingPeriod[] = ['monthly', 'yearly'];

export type SubscriptionPlanId = 'pro' | 'aiCoach';

export interface SubscriptionPlan {
  /** Stable id — also the plan tab id and the `plans.<id>` i18n key segment. */
  id: SubscriptionPlanId;
  /** RevenueCat entitlement this plan grants (drives "current plan" state). */
  entitlement: EntitlementId;
  /**
   * Tier rank (higher = more inclusive). A higher plan's entitlement subsumes the
   * lower ones in RevenueCat (AI Coach also grants `pro`), so this disambiguates a
   * user's actual plan: the highest tier whose entitlement is active is "current",
   * lower tiers are shown as "included" rather than "current".
   */
  tier: number;
  /** RevenueCat package `lookup_key` per billing period. */
  packages: Record<BillingPeriod, string>;
  /**
   * Leaf i18n keys under `plans.<id>.features` in the `subscription` namespace,
   * rendered as a check-row list.
   */
  featureKeys: readonly string[];
}

// AI Coach is listed first so it is the default-selected plan (SUBSCRIPTION_PLANS[0])
// and the primary tab.
export const SUBSCRIPTION_PLANS: readonly SubscriptionPlan[] = [
  {
    id: 'aiCoach',
    entitlement: ENTITLEMENTS.ai,
    tier: 2,
    packages: {
      monthly: '$rc_monthly',
      yearly: '$rc_annual',
    },
    featureKeys: [
      'mealChat',
      'workoutChat',
      'generalChat',
      'nutritionQa',
      'credits',
      'buyCredits',
      'mcpAccess',
    ],
  },
  {
    id: 'pro',
    entitlement: ENTITLEMENTS.pro,
    tier: 1,
    packages: {
      monthly: '$rc_custom_pro_monthly',
      yearly: '$rc_custom_pro_annual',
    },
    featureKeys: [
      'barcodeScanning',
      'customFoods',
      'customExercises',
      'personalRecords',
      'favorites',
      'unlimitedHistory',
      'macroTargets',
      'restApi',
    ],
  },
];

/** Look up a plan by id (throws never — ids are exhaustive in the config). */
export const getSubscriptionPlan = (id: SubscriptionPlanId): SubscriptionPlan =>
  SUBSCRIPTION_PLANS.find((plan) => plan.id === id) ?? SUBSCRIPTION_PLANS[0];
